import {Environment, GrowthbookConfig} from '../shared/types';
import {
  MPUserIdentities,
  mParticleInit,
  mParticleLogEvent,
  mParticleEventType,
  MPInitOptions,
} from '@treatwell/ui';
import {fetchConfig} from './api/config';
import {
  GrowthBookAttributes,
  growthbookInitialiseClient,
  getDeviceId,
} from '@treatwell/growthbook-js/client';
import {RainbowResponseChannel} from '../server/rainbow/types';
import {
  AccountInformationApiResponse,
  fetchAccountInformation,
} from './api/user';
import {Context, GrowthBook} from '@growthbook/growthbook';
import {fetchCachedChannel} from './api/channel';
import {getUrlInfo} from './utils/getUrlInfo';
import {getLanguageCode} from '../shared/utils/getHeaderLanguageCode';
import {initialiseDatadogWithConfig} from './datadog/initialiseClient';
import {
  configureTolgee,
  fetchTranslation,
  getLocale,
  initI18next,
} from '@treatwell/tolgee/client';
import tolgeeConfig from '../tolgee.config.json';
import {getFallbackLocaleLoader} from './utils/getFallbackLocaleLoader';
import translationEn from '../locales/en.json';

let growthbookResolve: (value: GrowthBook) => void;
const growthbookPromise = new Promise<GrowthBook>((resolve) => {
  growthbookResolve = resolve;
});

export function getGrowthbook() {
  return growthbookPromise;
}

let translationsReadyResolve: () => void;
const translationsReadyPromise = new Promise<void>((resolve) => {
  translationsReadyResolve = resolve;
});

export function getTranslationsReady() {
  return translationsReadyPromise;
}

export async function initialiseCoreLibraries() {
  const {pathname} = getUrlInfo(location.href);
  const languageCode = getLanguageCode(pathname);
  const [config, channel, user] = await Promise.allSettled([
    fetchConfig(),
    fetchCachedChannel({languageCode}),
    fetchAccountInformation(),
    initialiseDatadogWithConfig(),
  ]);

  const {channelAttributes, userAttributes, userIdentities, channelLocale} =
    extractAttributes(channel, user);

  let environment: Environment = 'dev';
  let growthbookConfig: GrowthbookConfig[Environment] = {
    clientKey: '',
    apiHost: '',
  };

  if (config.status === 'fulfilled') {
    environment = config.value.environment;
    growthbookConfig = config.value.growthbook;
  }

  configureTolgee({
    ...tolgeeConfig,
    isEnvProduction: environment === 'prod',
    getFallbackLocaleLoader,
  });

  const locale = getLocale(channelLocale ?? 'en');
  await fetchTranslation(locale)
    .then((translations) => initI18next(locale, translations))
    .catch((error) => {
      initI18next('en', translationEn);
      console.error(
        `recover from static locales failure - using en\n\n${error.message}`,
      );
    });
  translationsReadyResolve();

  const deviceId = await getDeviceId();
  initialiseMParticle(environment, userIdentities, deviceId);

  const growthbook = await growthbookInitialiseClient(
    getGrowthBookConfig(environment, growthbookConfig),
    deviceId,
    {
      growthbookAttributes: {
        channel: channelAttributes,
        user: userAttributes,
      },
      activationEvent: {
        type: 'mparticle',
        send(defaultAttributes) {
          mParticleLogEvent(
            '$experiment_started',
            mParticleEventType.Other,
            defaultAttributes,
          );
        },
      },
    },
  );

  growthbookResolve(growthbook);
}

function getGrowthBookConfig(
  environment: Environment,
  growthbookConfig: GrowthbookConfig[Environment],
) {
  const config: Context = {
    clientKey: growthbookConfig.clientKey,
    apiHost: growthbookConfig.apiHost,
  };
  if (environment !== 'prod') {
    config.enableDevMode = true;
    config.disableCache = true;
  }
  return config;
}

function extractAttributes(
  channel: PromiseSettledResult<RainbowResponseChannel>,
  user: PromiseSettledResult<AccountInformationApiResponse>,
) {
  const channelAttributes: GrowthBookAttributes['channel'] = {};
  const userAttributes: GrowthBookAttributes['user'] = {};
  const userIdentities: MPUserIdentities = {};

  let channelLocale: string | undefined;
  if (channel.status === 'fulfilled') {
    channelAttributes.country = channel.value.country.countryCode;
    channelLocale = channel.value.locale;
  }

  if (
    user.status === 'fulfilled' &&
    user.value.loginStatus === 'account_authenticated'
  ) {
    const {accountId, email} = user.value;
    userAttributes.account_id = String(accountId);
    userAttributes.is_authenticated = true;
    userIdentities.customerid = `${accountId}`;
    userIdentities.email = email;
  } else {
    userAttributes.is_authenticated = false;
  }

  return {channelAttributes, userAttributes, userIdentities, channelLocale};
}

async function initialiseMParticle(
  environment: Environment,
  userIdentities: MPUserIdentities,
  deviceId: string,
) {
  const isWidget = /^(?:widget|book|buchung)\.treatwell/.test(location.host);

  if (isWidget) {
    const growthbook = await getGrowthbook();
    const isMparticleWidgetTrackingEnabled = growthbook.getFeatureValue(
      'ff-web-checkout__mparticle-widget',
      false,
    );

    if (!isMparticleWidgetTrackingEnabled) {
      return;
    }
  }

  try {
    // Download the mParticle SDK before the kits, since the kits depend on it
    const mParticle = await import(
      /* webpackChunkName: "mparticle_web-sdk" */ '@mparticle/web-sdk'
    );

    const options: MPInitOptions = {
      isDevelopmentMode: environment !== 'prod',
      deviceId,
      isWidget,
    };

    // Skip the kits when running inside a widget
    if (!isWidget) {
      const [onetrustKit, brazeKit] = await Promise.all([
        import(
          /* webpackChunkName: "mparticle_web-onetrust-kit" */ '@mparticle/web-onetrust-kit'
        ),
        import(
          /* webpackChunkName: "mparticle_web-braze-kit" */ '@mparticle/web-braze-kit'
        ),
      ]);

      // Register kits before initializing mParticle
      onetrustKit.register(options);
      brazeKit.default.register(options);
    }

    mParticleInit(mParticle.default, options, userIdentities);
  } catch (error) {
    console.error('Failed to initialize mParticle:', error);
  }
}
